import {
  ControlApiInterface,
  Control,
  StatisticsApi,
  SkipControlBody
} from "../swagger";
import { JsonObject } from "../../types";
import {
  AgentSelfStatistics,
  ControlAdditionalData,
  ControlResultFps,
  ControlResultNfps,
  ControlReview,
  DecisionsStatistics
} from "../model";
import ControlReviewDao from "./ControlReviewDao";

export class SwaggerControlReviewDao implements ControlReviewDao {
  private readonly _controlReviewSwaggerClient: ControlApiInterface;
  private readonly _statisticsSwaggerClient: StatisticsApi;

  public constructor(
    controlReviewSwaggerClient: ControlApiInterface,
    statisticsSwaggerClient: StatisticsApi
  ) {
    this._controlReviewSwaggerClient = controlReviewSwaggerClient;
    this._statisticsSwaggerClient = statisticsSwaggerClient;
  }

  public async emitFps(
    controlId: string,
    payload?: ControlResultFps
  ): Promise<void> {
    try {
      await this._controlReviewSwaggerClient.emitFps(controlId, payload as any);
    } catch (error) {
      throw error;
    }
  }

  async skipControl(
    controlId: string,
    payload?: SkipControlBody
  ): Promise<void> {
    try {
      const res = await this._controlReviewSwaggerClient.skipControl(
        controlId,
        payload as any
      );

      return res.data;
    } catch (error) {
      throw error;
    }
  }

  public async emitNfps(
    controlId: string,
    payload?: ControlResultNfps
  ): Promise<void> {
    try {
      await this._controlReviewSwaggerClient.emitNfps(
        controlId,
        payload as any
      );
    } catch (error) {
      throw error;
    }
  }

  public async getControlReview(force?: number): Promise<ControlReview> {
    try {
      const res = await this._controlReviewSwaggerClient.getControl(force);

      return res.data as ControlReview;
    } catch (error) {
      throw error;
    }
  }

  public async saveControlReview(
    controlId: string,
    payload?: ControlAdditionalData
  ): Promise<ControlReview> {
    try {
      const res = await this._controlReviewSwaggerClient.saveControl(
        controlId,
        payload
      );

      return res.data as ControlReview;
    } catch (error) {
      throw error;
    }
  }

  public async sendFollower(
    controlId: string,
    payload?: JsonObject
  ): Promise<JsonObject> {
    try {
      const res = await this._controlReviewSwaggerClient.sendFollower(
        controlId,
        payload
      );

      return res.data as JsonObject;
    } catch (error) {
      throw error;
    }
  }

  public async getAgentSelfStatistics(): Promise<AgentSelfStatistics> {
    try {
      const res = await this._statisticsSwaggerClient.getAgentSelfStatistics();

      return res.data;
    } catch (error) {
      throw error;
    }
  }

  public async getDayAgentsStatistics(): Promise<JsonObject[]> {
    try {
      const res = await this._statisticsSwaggerClient.getDayAgentsStatistics();

      return res.data as JsonObject[];
    } catch (error) {
      throw error;
    }
  }
  /**
   *
   * @summary getDayDecisionsStatistics
   * Warning ! Les champs 'expired' et 'others' renvoient toujours undefined
   * Fonction dépréciée => Utliser getDecisionStatistics() à la place
   */
  public async getDayDecisionsStatistics(): Promise<DecisionsStatistics> {
    try {
      let date = new Date();
      date.setHours(0);
      const startDate = date.toISOString();
      date.setHours(23);
      date.setMinutes(59);
      date.setSeconds(59);
      const endDate = date.toISOString();

      return this.getDecisionStatistics(startDate, endDate, "day");
    } catch (error) {
      throw error;
    }
  }

  public async getDayHourlyStatistics(): Promise<JsonObject[]> {
    try {
      let date = new Date();
      date.setHours(0);
      const startDate = date.toISOString();
      date.setHours(23);
      date.setMinutes(59);
      date.setSeconds(59);
      const endDate = date.toISOString();

      const res = await this._statisticsSwaggerClient.getDecisionStatistics(
        startDate,
        endDate,
        "hour"
      );

      return res.data.activity!.map((item: any) => {
        return {
          date: item.date,
          total: item.FPS + item.NFPS + item.VERIFY,
          processed: item.FPS + item.NFPS
        };
      }) as JsonObject[];
    } catch (error) {
      throw error;
    }
  }

  public async getDecisionStatistics(
    startDate: string,
    endDate: string,
    activity?: "day" | "hour",
    options?: any
  ): Promise<Omit<DecisionsStatistics, "expired" | "others">> {
    const res = await this._statisticsSwaggerClient.getDecisionStatistics(
      startDate,
      endDate,
      activity,
      options
    );
    return {
      agentSent: res.data.total?.VERIFY,
      emittedFps: res.data.total?.FPS,
      emittedNfps: res.data.total?.NFPS
    };
  }

  public async getDayLapiStatistics(): Promise<JsonObject[]> {
    try {
      const res = await this._statisticsSwaggerClient.getDayLapiStatistics();

      return res.data as JsonObject[];
    } catch (error) {
      throw error;
    }
  }
}

export default SwaggerControlReviewDao;
